import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  BarChart3,
  ClipboardList,
  HandCoins,
  Paperclip,
  Percent,
  PiggyBank,
  Receipt,
  Languages,
  ReceiptText,
  Repeat,
  UserCheck,
  Wallet,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Finance is finance-facing — mirrors the Finance nav gate. */
const FINANCE_ROLES: Role[] = ['super_admin', 'founder', 'accountant'];

export const FINANCE_GUIDES: Guide[] = [
  {
    slug: 'finance-overview',
    title: 'How money flows',
    module: 'finance',
    summary: 'Catalog → billing → income, and expenses → payroll → reports.',
    icon: Wallet,
    roles: FINANCE_ROLES,
    body: `**Finance** is where the money is tracked — what comes in, what goes out, and
what it all adds up to.

\`\`\`
 SERVICE CATALOG ─▶ BILLING (client invoices) ─▶ INCOME ─┐
                                                          ├─▶ REPORTS
 EXPENSES · RECURRING · PAYROLL (staff invoices) ─▶ OUT ─┘   (P&L, revenue)
\`\`\`

### The pieces
- **Service Catalog** — your rate card; the source of prices.
- **Billing** — invoices you send clients (and payables you owe).
- **Staff Invoices & Payroll** — how the team gets paid.
- **Income / Expenses / Recurring Expenses** — the money ledgers.
- **Commissions, Taxes, Budgeting, Reports** — the rest of the finance toolkit.

### Two rules that run through everything
- **Money is multi-currency, based in USD.** Every amount carries its currency and
  the FX rate captured **at the time of the transaction** — figures are never
  silently re-converted.
- **Issued records are snapshots.** Once an invoice or proposal is issued, its
  lines are frozen — editing the catalog later never changes history.`,
  },
  {
    slug: 'finance-service-catalog',
    title: 'Service Catalog',
    module: 'finance',
    summary: 'Your org-wide rate card — the source of prices.',
    icon: ClipboardList,
    roles: FINANCE_ROLES,
    body: `The **Service Catalog** is your rate card: the services you sell and what they
cost.

### How to use it
- Add services with a **base rate (USD)** and an optional **per-currency override**
  (e.g. EUR, GBP).
- Pick a service when adding a line to a **proposal** or **invoice** — it fills in
  the description and rate (you can still free-type).

### Why it's here — snapshots
When a service is used on a proposal/invoice, its **name and rate are copied
(snapshotted)**. Editing or deleting a catalog entry later never changes an
already-issued document — so past offers and invoices stay exactly as agreed.`,
  },
  {
    slug: 'finance-billing',
    title: 'Billing',
    module: 'finance',
    summary: 'Client invoices you send (and payables you owe).',
    icon: ReceiptText,
    roles: FINANCE_ROLES,
    body: `**Billing** is your client invoices — **receivables (AR)** you send out, and
**payables (AP)** you owe.

### The lifecycle
**Draft → Issued → Paid** (or **Void**). Everything is editable while **draft**;
**issuing** locks the invoice into a snapshot.

### Building a draft
1. Create an invoice for a client — optionally link a **deal**, one or more
   **projects** (the client's own or ones with no client), and a billing
   **period** (start/end date).
2. Add line items. Each line is **billed by** one of:
   - **Time** — enter **hours + minutes**; the invoice shows \`Hh Mm\` (e.g.
     \`7h 30m\`) while the exact decimal drives the maths.
   - **Quantity** — a plain number with a free-text **unit** ("month", "license",
     "VPS") and a **unit rate** — for fixed-price items.
3. Adjust lines in place: **edit a description inline**, **remove** a line, or
   **reorder** with the up/down arrows — all **draft-only**.

### Issue, then share the PDF
- **Issue** locks the lines and the document into a **frozen snapshot**.
- A draft shows **Generate** (build / refresh the PDF); an issued invoice shows
  **View** — the exact PDF captured at issue, **never regenerated** — plus
  **Download** to send it.
- **Mark paid** when the client pays.

### Changing an issued invoice
Issued invoices are read-only by design. To correct one, **Revert to draft**, make
the change, and **issue again** — producing a fresh snapshot.

### Worth knowing
- Marking a receivable **Paid** **realizes the linked deal's sales commission**.
- Filter the list by **period** (quarter / month / custom dates) to pull a run of bills.
- The PDF can bundle **worklog timesheets** and **external attachments** — see
  *Invoice documents & attachments*.
- Totals roll up in **base USD**; per-line currency + FX are preserved.

### Why it's here
A clean, auditable record of every client bill from draft to paid — and the
trigger that turns won deals into recognised income and earned commission.`,
  },
  {
    slug: 'finance-invoice-documents',
    title: 'Invoice documents & attachments',
    module: 'finance',
    summary: 'Bundle worklog timesheets and external PDFs into the invoice document.',
    icon: Paperclip,
    roles: FINANCE_ROLES,
    body: `A client invoice isn't only the line items — its **PDF can bundle
supporting pages**, so the client gets the bill *and* the evidence in one file.

\`\`\`
 INVOICE  +  WORKLOG TIMESHEETS  +  ATTACHED PAGES  ─▶  one merged PDF
             (projects + period)     (uploaded PDFs)
\`\`\`

### Worklog timesheets (automatic)
- Give the invoice its **Projects** and a **Period** (start/end). The generated PDF
  then appends a **timesheet per project and employee** over that period —
  the hours behind what you're billing. Only **billable** time is included;
  entries marked non-billable never reach the client.
- **Time is never billed twice.** Hours another invoice already billed are left
  out — the **Worklogs** card names that invoice. Once you **issue**, the attached
  worklogs are **locked** (nobody can edit or delete them) until you revert the
  invoice to draft; a **paid** invoice locks them for good.
- **No project, or no period → nothing is attached.** It's opt-in simply by
  filling those fields.

### Attached pages (manual)
- Under **Attached pages**, upload external PDFs — e.g. an **Everhour** time
  report — to **merge into the invoice PDF**.
- These live only on the invoice document; they are **not** saved as formal
  **Attachments** and don't clutter the file store.

### One document, frozen at issue
- **Generate** stitches everything into a single PDF: invoice → worklog pages →
  attached pages.
- On **Issue**, that merged document is **snapshotted**. **View** always shows the
  frozen version; **Revert to draft** to rebuild it.

### Why it's here
Clients ask "what am I paying for?" Bundling the timesheets and any external time
report into the same PDF answers that up front — fewer back-and-forths, and a
self-contained record of the bill and its backing detail.`,
  },
  {
    slug: 'finance-staff-invoices',
    title: 'Staff Invoices',
    module: 'finance',
    summary: 'How the team gets paid — salary becomes a signed invoice, then a payslip.',
    icon: HandCoins,
    roles: FINANCE_ROLES,
    body: `**Staff Invoices** are how people get paid — everyone is paid **by invoice**.

### The flow
\`\`\`
 SALARY ─▶ hourly rate ─▶ STAFF INVOICE ─▶ employee signs ─▶ PAID ─▶ PAYSLIP
\`\`\`
1. A salary is turned into an **hourly rate** and a staff invoice is **drafted**.
2. Generate the **PDF** and **Issue** it — the employee reviews and **signs** it in My Space.
3. **Mark paid** — it becomes a **payslip** the employee can download.

### Who can do what
Viewing and managing staff invoices is a **finance** responsibility; the employee
only signs.

### Worth knowing
- Once you pick the employee and the period, the form shows their **logged time**
  for that period — the total and a per-project split. **Use logged hours** fills
  the hours in; if you type something else, a warning shows the difference (you can
  still keep your figure). All logged time counts here, billable or not.
- The **hourly rate** fills in from the employee's salary in effect at the
  **period end** ÷ 168h (a raise from a later date isn't used), in the salary's
  **currency**, and follows the period end when you change it. If you chose another
  currency, or no salary is in effect by then,
  no rate is filled and a note under the field says why — enter the rate yourself
  (with another currency the invoice won't save until you do).
- Time-based lines display as \`Hh Mm\` (e.g. \`7h 30m\`) — the exact decimal still
  drives the pay maths.
- Filter the staff-invoice list **by employee** to jump straight to one person's
  invoices.
- The PDF is a **bilingual contractor invoice** — the employee is the supplier
  ("Invoiced by"), the company the customer. See *Contractor invoice setup
  (FOP / PE)* for the fields that feed it.

### Why it's here
Paying by signed invoice gives a clean, mutually-agreed, auditable pay record for
every period — no ambiguity about hours or amounts.`,
  },
  {
    slug: 'finance-contractor-invoice-setup',
    title: 'Contractor invoice setup (FOP / PE)',
    module: 'finance',
    summary: 'Fill four forms once — every staff invoice renders as a bilingual supplier invoice.',
    icon: Languages,
    roles: ['super_admin', 'founder', 'accountant', 'hr'],
    body: `Staff-invoice PDFs follow the standard **cross-border private-entrepreneur
invoice**: the employee is the **supplier** ("Invoiced by / Виконавець"), the
company is the **customer** ("Invoiced to / Замовник"). The company prepares the
invoice on the contractor's behalf (self-billing); the employee **signing it in
My Space is their acceptance**. The contractor's bank and tax records key off
this document, so the details below matter.

**One switch decides the language**: when the supplier's tax profile country is
**UA**, every block prints an English column and a Ukrainian column (blank
local-language fields fall back to English). Any other country produces a clean
English-only invoice from the same template — so it works for a Ukrainian ФОП,
a Polish JDG, or any other private entrepreneur without per-country setup.

### 1. Organization → Settings (admin)
| Field | Feeds |
|---|---|
| Legal name / **Legal name (local language)** | "Invoiced to / Замовник" |
| Address / **Address (local language)** | customer address lines |
| Registration / Tax ID | "VAT number / Номер платника ПДВ" |

### 2. Employee record → Invoicing identity (HR)
| Field | Feeds |
|---|---|
| **Legal name** | supplier name, e.g. "Natural person-entrepreneur Rybonka Stanislav Mykolaiovych" — full designation incl. patronymic |
| **Legal name (local language)** | "ФОП Рибонька Станіслав Миколайович" |
| Address + **Address (local language)** | supplier address lines |
| **Contract number** + **Contract date** | the "Under the Contract on rendering of services…" reference line — banks match cross-border transfers against it |

### 3. Tax profile (Finance → Taxes)
| Field | Feeds |
|---|---|
| Tax ID | "Individual Tax Number / ІПН" + "ITN CODE" in the banking block |
| **Tax status** (+ local) | printed verbatim, e.g. "is not a VAT payer / не є платником ПДВ" |

### 4. Employee bank account (HR → employee → Bank accounts)
| Field | Feeds |
|---|---|
| IBAN, bank name, SWIFT, routing (MFO), currency | "BANKING DETAILS / Банківські реквізити" |
| **Intermediary bank + SWIFT + beneficiary account** | the correspondent route — needed for EUR/USD wires into e.g. a UA account |

### What's automatic
- **Payment-as-acceptance clauses** print on every invoice in both languages —
  payment confirms the services, their acceptance, and final settlement, so no
  separate monthly acceptance act is needed.
- The **VAT / ПДВ** row, totals, hours and period localise themselves.
- Issued PDFs are **frozen snapshots** — changing any field affects only
  invoices generated afterwards.

### Checklist for a new contractor
1. Org settings filled once (usually already done).
2. Employee: legal names, addresses, contract № + date.
3. Tax profile: tax ID + tax status.
4. Bank account: IBAN + bank + intermediary route, marked **default**.
5. Draft a staff invoice → **Generate PDF** → check both columns → **Issue**.`,
  },
  {
    slug: 'finance-commissions',
    title: 'Commissions',
    module: 'finance',
    summary: 'Sales commission earned on deals — realized when the client pays.',
    icon: Percent,
    roles: FINANCE_ROLES,
    body: `**Commissions** is the org-wide view of sales commission (sales reps see only
their own in My Space).

### How it works
- A deal's **owner** earns its **commission %** — but only if the owner is flagged
  **commission-eligible** (see *Setting up sales commission*).
- Each deal has a **commission basis** that decides *what* it pays on:
  **first client invoice (new clients only)** or **all paid invoices**.
- When the qualifying invoice is **paid**, the commission is **realized**; before
  that it's **projected**.
- From here, finance can **pay out** or **void** a commission.

### Why it's here
A transparent, auditable record of what the sales team has earned and what's been
paid — tied directly to invoices that actually cleared, not promises.`,
  },
  {
    slug: 'finance-commission-setup',
    title: 'Setting up sales commission',
    module: 'finance',
    summary: 'Who earns, at what rate, and what each deal pays on.',
    icon: UserCheck,
    roles: FINANCE_ROLES,
    body: `Sales commission has **three layers** — who earns, how much, and on what.
Commission is **opt-in**: nothing accrues until you switch a person on.

\`\`\`
 ELIGIBILITY (who)  ─▶  RATE (how much)  ─▶  BASIS (on what)  ─▶  paid invoice ─▶ EARNED
 People → employee      deal % (prefilled)   per-deal setting
\`\`\`

### 1. Turn the person on — People → employee → Salary tab
- Flip **Commission eligible** on. It's **off by default**, so a new hire (or a
  salaried lead-gen / SDR) never accrues until you enable them.
- Set their **Default commission %** — this **prefills** new deals they own.
- Only shows for people in a **sales role** (Sales Manager / Lead Gen).

### 2. Set the rate & basis on the deal — CRM → Deal
- **Commission %** — prefilled from the owner's default; **override** it here for a
  one-off rate.
- **Commission basis** — how the commission is earned:
  - **First client invoice (new clients only)** — pays **once**, on the client's
    **first paid invoice**, and only if **this deal landed the new client**. A
    returning-client / expansion deal earns **nothing**.
  - **All paid invoices** — pays on **every** paid invoice on the deal.
- New deals inherit the org default basis — set it under **Settings → Default
  commission basis**.

### 3. It pays out when the client pays
Won deal → **projected** (pending). Client pays the qualifying invoice → the
commission is **realized / earned**. Finance then **pays it out** (see *Commissions*).

### Who can do what
Eligibility, default rate, per-deal rate and basis are all set by **Founder,
Super Admin or Accountant**. A salesperson can **see** their rate but can't change
it — their pay isn't self-served.

### Why it's here
Commission is opt-in and policy-driven: you decide who's on the plan, and the
new-logo basis rewards **landing clients** rather than every recurring invoice —
without hand-tracking any of it.`,
  },
  {
    slug: 'finance-recurring-expenses',
    title: 'Recurring Expenses',
    module: 'finance',
    summary: 'Templates that auto-generate regular expenses (rent, SaaS, salaries).',
    icon: Repeat,
    roles: FINANCE_ROLES,
    body: `**Recurring Expenses** are templates for costs that repeat — rent, software
subscriptions, retainers, salaries.

### How to use it
- Define a recurring expense (amount, currency, cadence, optional employee link).
- The system **generates the actual expense entries** on schedule, so they land in
  the Expenses ledger without re-entry.

### Why it's here
Most outgoings are predictable. Automating them keeps the books complete and your
cost picture current — without someone keying the same expense every month.`,
  },
  {
    slug: 'finance-income',
    title: 'Income',
    module: 'finance',
    summary: 'The ledger of money recognised as it comes in.',
    icon: ArrowDownToLine,
    roles: FINANCE_ROLES,
    body: `**Income** is the ledger of money coming in — recognised revenue, typically
flowing from paid invoices.

### How to use it
- Review income entries and their currency.
- The **per-page totals** are shown **per currency** (not blended) — each currency
  is summed on its own, so nothing is silently FX-converted.

### Why it's here
A clear record of what the business actually earned, in the currencies it was
earned in — the top line of your P&L.`,
  },
  {
    slug: 'finance-expenses',
    title: 'Expenses',
    module: 'finance',
    summary: 'The ledger of money going out, by category and (optionally) person.',
    icon: ArrowUpFromLine,
    roles: FINANCE_ROLES,
    body: `**Expenses** is the ledger of money going out — categorised costs, some linked
to an employee.

### How to use it
- Add or review expenses; assign a **category** (create one inline if it's missing).
- Link an expense to an **employee** where relevant (e.g. a reimbursement or cost).
- **Per-page totals** are shown **per currency**, never blended.

### Why it's here
The bottom half of your P&L — knowing exactly where money goes, grouped so you can
see and control it.`,
  },
  {
    slug: 'finance-payroll',
    title: 'Payroll',
    module: 'finance',
    summary: 'Run pay periods and produce payslips.',
    icon: Banknote,
    roles: FINANCE_ROLES,
    body: `**Payroll** is where pay periods are run and **payslips** are produced for the
team.

### How it fits together
Payroll sits on top of the **staff-invoice** flow — a signed, paid staff invoice
becomes the employee's payslip. Payroll runs group these so a period can be
processed and posted to the ledger together.

### Why it's here
A structured, repeatable way to pay everyone each period and keep an auditable
trail of what was paid, to whom, when.`,
  },
  {
    slug: 'finance-taxes',
    title: 'Taxes',
    module: 'finance',
    summary: 'Record-only tax data — figures you enter, no calculation engine.',
    icon: Receipt,
    roles: FINANCE_ROLES,
    body: `**Taxes** is **record-only**. The system stores the tax figures and
jurisdictions you enter — it does **not** calculate withholding or brackets.

### How to use it
- Record tax profiles and rates against the relevant **country** (full ISO list).
- Capture the numbers your accountant works from; the platform keeps them, it
  doesn't compute them.

### Why it's here
Tax rules differ everywhere and change often. Rather than bake in a fragile
calculation engine, we keep an accurate, auditable **record** for reporting and
hand the maths to the experts.`,
  },
  {
    slug: 'finance-budgeting',
    title: 'Budgeting',
    module: 'finance',
    summary: 'Set budgets to plan and track against actuals.',
    icon: PiggyBank,
    roles: FINANCE_ROLES,
    body: `**Budgeting** is where you set financial plans to measure reality against.

### How to use it
- Record budgets for the areas you want to control.
- Compare them against the actuals flowing through Income and Expenses.

### Why it's here
A budget turns "how are we doing?" into a concrete answer — early warning when
spend or revenue drifts from plan.`,
  },
  {
    slug: 'finance-reports',
    title: 'Reports',
    module: 'finance',
    summary: 'P&L, revenue by client and the headline finance numbers.',
    icon: BarChart3,
    roles: FINANCE_ROLES,
    body: `**Reports** pulls the finance picture together — all figures in **base USD**.

### What you'll find
- **Income, Expenses, Net** and an **MRR estimate** (trailing 3-month average).
- **Monthly P&L** — income, expense and net by month.
- **Revenue by client** — recognised income grouped by company, with each client's
  share (and a CSV export).

### Why it's here
One management view of profitability and where revenue comes from — the numbers
leadership needs without exporting and stitching spreadsheets.`,
  },
];
